/**
 * Organizations view model (super-admin). List endpoint returns every tenant,
 * so the screen pages and filters here.
 */

export const PAGE_SIZE = 50;

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export function initials(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return 'OR';
  return parts.slice(0, 2).map((part) => part[0]).join('').toUpperCase();
}

export function planLabel(plan) {
  const raw = String(plan || 'free').trim();
  if (!raw) return 'Free';
  return raw.charAt(0).toUpperCase() + raw.slice(1);
}

export function joinedLabel(createdAt) {
  if (!createdAt) return '';
  try {
    const text = new Date(createdAt).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    return text ? `joined ${text}` : '';
  } catch {
    return '';
  }
}

export function createdLabel(createdAt) {
  if (!createdAt) return '—';
  try {
    const d = new Date(createdAt);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toISOString().slice(0, 10);
  } catch {
    return '—';
  }
}

export function statusWord(tenant) {
  if (!tenant) return 'Inactive';
  if (tenant.approval_pending) return 'Pending';
  return tenant.is_active === false ? 'Inactive' : 'Active';
}

export function statusTone(tenant) {
  const word = statusWord(tenant);
  if (word === 'Active') return 'active';
  if (word === 'Pending') return 'pending';
  return 'inactive';
}

export function subline(tenant) {
  const plan = planLabel(tenant && tenant.plan);
  const joined = joinedLabel(tenant && tenant.created_at);
  return joined ? `${plan} · ${joined}` : plan;
}

export function filterOrgs(tenants, search) {
  const list = Array.isArray(tenants) ? tenants : [];
  const q = String(search || '').trim().toLowerCase();
  if (!q) return [...list];
  return list.filter((row) => {
    const hay = [row.name, row.slug, row.plan].join(' ').toLowerCase();
    return hay.includes(q);
  });
}

export function pageOf(rows, shown) {
  const list = Array.isArray(rows) ? rows : [];
  const count = Math.min(list.length, Math.max(PAGE_SIZE, Number(shown) || PAGE_SIZE));
  return {
    rows: list.slice(0, count),
    more: list.length > count,
    remaining: Math.max(0, list.length - count),
  };
}

export function unreadLabel(count) {
  const n = Number(count) || 0;
  if (n <= 0) return '';
  return n > 99 ? '99+' : String(n);
}

export function connectorSummary(tenant) {
  const health = tenant && tenant.connector_health;
  if (!health || !health.total) return { lead: 'No connectors ', accent: 'yet.' };
  const bits = [];
  if (health.live) bits.push(`${health.live} live`);
  if (health.degraded) bits.push(`${health.degraded} degraded`);
  let lead = bits.join(' · ') || 'No connector health data';
  if (health.degraded_names && health.degraded_names.length) {
    lead += ` (${health.degraded_names.join(', ')})`;
  }
  return { lead, accent: '' };
}

export function planBillingLine(tenant) {
  const seats = Number(tenant && tenant.user_count) || 0;
  const seatWord = seats === 1 ? 'seat' : 'seats';
  return `${planLabel(tenant && tenant.plan)} · ${seats} ${seatWord}`;
}

export function sessionTitle(session) {
  if (!session) return '—';
  if (session.slot_time) {
    try {
      const dt = new Date(session.slot_time);
      if (!Number.isNaN(dt.getTime())) {
        const day = dt.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
        const time = dt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
        return `${day} at ${time}`;
      }
    } catch { /* fall through */ }
  }
  return session.slot_label || '—';
}

export function sessionDay(session) {
  try {
    const d = new Date(session.slot_time);
    if (Number.isNaN(d.getTime())) return '—';
    return String(d.getDate());
  } catch {
    return '—';
  }
}

export function statusPill(status) {
  const raw = String(status || 'upcoming').trim();
  if (!raw) return 'Upcoming';
  return raw.charAt(0).toUpperCase() + raw.slice(1);
}

export function weekdayName(weekday) {
  const n = Number(weekday);
  if (!Number.isInteger(n) || n < 0 || n > 6) return 'Day';
  return DAYS[n];
}

export function normalizeSlotTime(value) {
  const raw = String(value || '').trim();
  const match = raw.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return '';
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (!Number.isInteger(hour) || hour < 0 || hour > 23) return '';
  if (!Number.isInteger(minute) || minute < 0 || minute > 59) return '';
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

export function validateCreate(form) {
  const orgName = String((form && form.orgName) || '').trim();
  const adminName = String((form && form.adminName) || '').trim();
  const adminEmail = String((form && form.adminEmail) || '').trim().toLowerCase();
  const password = String((form && form.password) || '');
  const chatLanguage = String((form && form.chatLanguage) || 'en').trim() || 'en';
  if (!orgName || !adminName || !adminEmail || !password) {
    return { error: 'All fields are required' };
  }
  if (!adminEmail.includes('@')) return { error: 'Enter a valid admin email' };
  if (password.length < 6) return { error: 'Password must be at least 6 characters' };
  return {
    body: {
      orgName,
      adminName,
      adminEmail,
      password,
      chatLanguage,
    },
  };
}

export function pickSelected(filtered, selectedId) {
  const list = Array.isArray(filtered) ? filtered : [];
  if (!list.length) return '';
  if (selectedId && list.some((row) => row.id === selectedId)) return selectedId;
  return list[0].id;
}

export function unreadTotal(tenants) {
  return (Array.isArray(tenants) ? tenants : [])
    .reduce((sum, row) => sum + (Number(row.unread_messages) || 0), 0);
}
